import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ScrollText, Search, Filter, Clock, User, Shield, FileText,
  Loader2, ChevronLeft, ChevronRight, AlertCircle, RefreshCw
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06 } } };
const item = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } } };

const ACTION_ICONS = {
  certificate_generated: FileText,
  role_changed: Shield,
  user_created: User,
  user_updated: User,
  permission_changed: Shield,
};

const ACTION_COLORS = {
  certificate_generated: { bg: "rgba(16,185,129,0.08)", color: "#10b981" },
  role_changed: { bg: "rgba(139,92,246,0.08)", color: "#8b5cf6" },
  user_created: { bg: "rgba(59,130,246,0.08)", color: "#3b82f6" },
  permission_changed: { bg: "rgba(239,68,68,0.08)", color: "#ef4444" },
  default: { bg: "rgba(100,116,139,0.08)", color: "#64748b" },
};

const AuditLogPage = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filterAction, setFilterAction] = useState("all");
  const [filterDays, setFilterDays] = useState("30");
  const limit = 20;

  useEffect(() => { fetchLogs(); fetchStats(); }, [page, filterAction, filterDays]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: page * limit, limit, days: filterDays });
      if (filterAction !== "all") params.set("action", filterAction);
      const res = await api.get(`/audit-logs?${params}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get("/audit-logs/stats");
      setStats(res.data);
    } catch (e) { console.error(e); }
  };

  const formatTime = (ts) => {
    if (!ts) return "—";
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const variant = user?.role === "super_admin" ? "super_admin" : "tenant_admin";
  const totalPages = Math.ceil(total / limit);

  const statCards = [
    { label: "Total Events", value: stats?.total || 0, icon: ScrollText, color: "#3b82f6", bg: "rgba(59,130,246,0.08)" },
    { label: "Today", value: stats?.today || 0, icon: Clock, color: "#10b981", bg: "rgba(16,185,129,0.08)" },
    { label: "This Week", value: stats?.this_week || 0, icon: AlertCircle, color: "#f59e0b", bg: "rgba(245,158,11,0.08)" },
  ];

  return (
    <AdminLayout title="Audit Logs" variant={variant}>
      <motion.div className="ent-page space-y-6" variants={container} initial="hidden" animate="show">
        {/* Header */}
        <motion.div variants={item} className="flex items-end justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="ent-icon-wrap" style={{ background: "rgba(100,116,139,0.08)" }}>
                <ScrollText className="w-5 h-5 text-slate-600" />
              </div>
              <h1 className="text-2xl font-extrabold tracking-tight text-slate-900" data-testid="audit-log-title">
                Audit Logs
              </h1>
            </div>
            <p className="text-sm text-slate-500 ml-14">Track all permission changes, role updates, and admin actions</p>
          </div>
          <Button variant="outline" size="sm" className="rounded-xl border-slate-200" onClick={() => { setPage(0); fetchLogs(); fetchStats(); }}>
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </motion.div>

        {/* Stats */}
        <motion.div variants={item} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {statCards.map((s, i) => (
            <div key={i} className="ent-stat" data-testid={`audit-stat-${i}`}>
              <div className="flex items-center gap-3">
                <div className="ent-icon-wrap" style={{ background: s.bg }}>
                  <s.icon className="w-5 h-5" style={{ color: s.color }} />
                </div>
                <div>
                  <div className="stat-num" style={{ fontSize: "1.5rem" }}>{s.value}</div>
                  <div className="stat-label">{s.label}</div>
                </div>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Filters */}
        <motion.div variants={item} className="flex gap-3 items-center">
          <Select value={filterAction} onValueChange={v => { setFilterAction(v); setPage(0); }}>
            <SelectTrigger className="w-[200px] rounded-xl border-slate-200" data-testid="audit-filter-action">
              <Filter className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
              <SelectValue placeholder="All Actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="certificate_generated">Certificate Generated</SelectItem>
              <SelectItem value="role_changed">Role Changed</SelectItem>
              <SelectItem value="permission_changed">Permission Changed</SelectItem>
              <SelectItem value="user_created">User Created</SelectItem>
              <SelectItem value="user_updated">User Updated</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterDays} onValueChange={v => { setFilterDays(v); setPage(0); }}>
            <SelectTrigger className="w-[150px] rounded-xl border-slate-200">
              <Clock className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-slate-400 ml-auto">{total} events</span>
        </motion.div>

        {/* Log List */}
        <motion.div variants={item} className="ent-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : logs.length === 0 ? (
            <div className="ent-empty">
              <div className="ent-empty-icon">
                <ScrollText className="w-8 h-8" style={{ color: "var(--hub-accent)" }} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1">No audit events found</h3>
              <p className="text-sm text-slate-500">Actions performed by admins will appear here</p>
            </div>
          ) : (
            <>
              <div className="flex items-center px-5 py-3 bg-slate-50/80 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <div className="w-48">Timestamp</div>
                <div className="flex-1">Action</div>
                <div className="w-40">User</div>
                <div className="w-32 text-right">Resource</div>
              </div>
              {logs.map((log, idx) => {
                const IconComp = ACTION_ICONS[log.action] || AlertCircle;
                const colors = ACTION_COLORS[log.action] || ACTION_COLORS.default;
                return (
                  <div key={log.id || idx} className="ent-table-row" data-testid={`audit-log-${idx}`}>
                    <div className="w-48 text-xs text-slate-400 font-medium">{formatTime(log.timestamp)}</div>
                    <div className="flex-1 flex items-center gap-3 min-w-0">
                      <div className="ent-icon-wrap shrink-0" style={{ background: colors.bg, width: 32, height: 32, borderRadius: 8 }}>
                        <IconComp className="w-4 h-4" style={{ color: colors.color }} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800">{log.action?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</p>
                        {log.details && <p className="text-xs text-slate-400 truncate">{log.details}</p>}
                      </div>
                    </div>
                    <div className="w-40 text-sm text-slate-600 truncate">{log.user_name || "System"}</div>
                    <div className="w-32 text-right text-xs text-slate-400">{log.resource_type}</div>
                  </div>
                );
              })}
            </>
          )}
        </motion.div>

        {/* Pagination */}
        {totalPages > 1 && (
          <motion.div variants={item} className="flex items-center justify-between">
            <span className="text-sm text-slate-400">
              Page {page + 1} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-xl" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" className="rounded-xl" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </AdminLayout>
  );
};

export default AuditLogPage;
